/**
 * Activity Dashboard Component
 * Displays user activity analytics and navigation history
 */

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import authService from '../services/authService';
import { formatTimestamp } from '../utils/dateUtils';

const ActivityDashboard = () => {
  const { user } = useContext(AuthContext);
  const [activities, setActivities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usernames, setUsernames] = useState([]);
  const [filter, setFilter] = useState({
    usernames: [], // Changed from username to usernames array
    activity_type: '',
    call_type: '', // Add call_type filter
    status_code: '',
    limit: 50,
    offset: 0
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'timestamp',
    direction: 'desc'
  });

  useEffect(() => {
    if (user) {
      loadUsernames();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadUserActivity();
      loadUserAnalytics();
    }
  }, [user, filter]);

  const loadUsernames = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await authService.getUsernames(token, false); // Automatic - page load
      setUsernames(response.usernames || []);
    } catch (err) {
      console.error('Error loading usernames:', err);
      // Don't show error for usernames, just use empty array
    }
  };

  const loadUserActivity = async () => {
    // Always use filterActivities for consistency now that it handles all cases properly
    await filterActivities(filter);
  };

  const loadUserAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      let analyticsData;

      // Determine which analytics to load based on filter
      if (filter.usernames.length === 0) {
        // Empty array means current user only
        analyticsData = await authService.getCurrentUserAnalytics(token, null, null, false); // Automatic
      } else if (filter.usernames.length === 1 && filter.usernames[0] === 'all') {
        // Special case: 'all' users (admin only)
        analyticsData = await authService.getUserAnalytics(token, 'all', null, null, false); // Automatic
      } else {
        // Multiple specific users - use new multi-user analytics endpoint
        analyticsData = await authService.getMultiUserAnalytics(token, filter.usernames, null, null, false); // Automatic
      }

      setAnalytics(analyticsData);
    } catch (err) {
      console.warn('Failed to load analytics:', err);
      // Don't show error to user, just log it and clear analytics
      setAnalytics(null);
    }
  };

  const filterActivities = async (filterParams) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setLoading(true);

      // Build search parameters - only include defined values
      const searchParams = {
        limit: filterParams.limit || 100,
        offset: filterParams.offset || 0
      };

      // Handle usernames filter
      if (filterParams.usernames.length === 0) {
        // Empty array means current user only
        searchParams.usernames = [user?.username];
      } else if (filterParams.usernames.includes('all')) {
        // If 'all' is selected, don't include usernames parameter to get all activities
        // Note: this requires admin permissions
      } else {
        // Specific users selected
        searchParams.usernames = filterParams.usernames;
      }

      // Handle activity type filter
      if (filterParams.activity_type) {
        searchParams.activity_type = filterParams.activity_type;
      }

      // Handle call type filter
      if (filterParams.call_type) {
        searchParams.call_type = filterParams.call_type;
      }

      // Handle status code filter
      if (filterParams.status_code) {
        searchParams.status_code = parseInt(filterParams.status_code);
      }

      const response = await authService.searchActivities(token, searchParams, true); // Manual - user filtering
      setActivities(response.activities || []);
    } catch (err) {
      console.error('Error searching activities:', err);
      setError(err.message || err.toString() || 'Failed to search activities');
    } finally {
      setLoading(false);
    }
  };

  const getActivityTypeColor = (type) => {
    switch (type) {
      case 'navigation': return 'primary';
      case 'api_call': return 'success';
      case 'login': return 'info';
      case 'logout': return 'warning';
      default: return 'secondary';
    }
  };

  const getCallTypeColor = (callType) => {
    switch (callType) {
      case 'manual': return 'warning';
      case 'automatic': return 'info';
      default: return 'secondary';
    }
  };

  const getCallTypeBadgeText = (callType) => {
    switch (callType) {
      case 'manual': return 'Manual';
      case 'automatic': return 'Auto';
      default: return 'N/A';
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const sortedActivities = React.useMemo(() => {
    if (!activities.length) return activities;

    return [...activities].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle different data types
      if (sortConfig.key === 'timestamp') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (sortConfig.key === 'status_code' || sortConfig.key === 'duration_ms') {
        const aNum = parseInt(aValue) || 0;
        const bNum = parseInt(bValue) || 0;
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // String comparison for other fields
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();

      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [activities, sortConfig]);

  if (!user) {
    return (
      <div className="alert alert-warning">
        Please log in to view your activity dashboard.
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h2>Activity Dashboard</h2>
          <p className="text-muted">View your navigation and API activity history</p>
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="row mb-4">
          <div className="col-md-8">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  Analytics for {
                    analytics.username === 'all' ? 'All Users' :
                      analytics.username === user?.username ? 'My Activities' :
                        analytics.username.startsWith('Multiple Users') ? analytics.username :
                          analytics.username
                  }
                </h5>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-3">
                    <div className="d-flex flex-column">
                      <h6 className="text-muted mb-1">Total Activities</h6>
                      <h3 className="text-primary mb-0">{analytics.total_activities}</h3>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex flex-column">
                      <h6 className="text-muted mb-1">Page Visits</h6>
                      <h3 className="text-info mb-0">{analytics.navigation_events}</h3>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex flex-column">
                      <h6 className="text-muted mb-1">API Calls</h6>
                      <h3 className="text-success mb-0">{analytics.api_calls}</h3>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex flex-column">
                      <h6 className="text-muted mb-1">Last Activity</h6>
                      <p className="text-dark mb-0 small">
                        {analytics.last_activity ? formatTimestamp(analytics.last_activity) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Most Visited Endpoints */}
          {analytics && analytics.most_visited_endpoints && analytics.most_visited_endpoints.length > 0 && (
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Most Visited Pages</h5>
                </div>
                <div className="card-body">
                  <ul className="list-group list-group-flush">
                    {analytics.most_visited_endpoints.slice(0, 5).map((endpoint, index) => (
                      <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>{endpoint.endpoint}</span>
                        <span className="badge bg-primary rounded-pill">{endpoint.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Filter Activities</h5>
              <div className="row">
                <div className="col-md-3">
                  <label className="form-label">Users</label>
                  <div className="dropdown">
                    <button
                      className="form-select text-start d-flex justify-content-between align-items-center"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      style={{
                        border: '1px solid #ced4da',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      <span className="text-truncate">
                        {filter.usernames.length === 0
                          ? 'My Activities'
                          : filter.usernames.includes('all')
                            ? 'All Users'
                            : `${filter.usernames.length} user${filter.usernames.length > 1 ? 's' : ''} selected`}
                      </span>
                    </button>
                    <ul className="dropdown-menu w-100" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      <li>
                        <div className="dropdown-item-text" onClick={(e) => e.stopPropagation()}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="current-user"
                              checked={filter.usernames.length === 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFilter({ ...filter, usernames: [] });
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="current-user">
                              <strong>My Activities</strong>
                            </label>
                          </div>
                        </div>
                      </li>

                      <li>
                        <div className="dropdown-item-text" onClick={(e) => e.stopPropagation()}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="all-users"
                              checked={filter.usernames.includes('all')}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFilter({ ...filter, usernames: ['all'] });
                                } else {
                                  setFilter({ ...filter, usernames: filter.usernames.filter(u => u !== 'all') });
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="all-users">
                              <strong>All Users</strong>
                            </label>
                          </div>
                        </div>
                      </li>
                      <>
                        <li><hr className="dropdown-divider" /></li>
                        {usernames.map((username) => (
                          <li key={username}>
                            <div className="dropdown-item-text" onClick={(e) => e.stopPropagation()}>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`user-${username}`}
                                  checked={filter.usernames.includes(username) && !filter.usernames.includes('all')}
                                  disabled={filter.usernames.includes('all')}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      // Add username if not already selected and 'all' is not selected
                                      const currentUsernames = filter.usernames.filter(u => u !== 'all');
                                      if (!currentUsernames.includes(username)) {
                                        setFilter({ ...filter, usernames: [...currentUsernames, username] });
                                      }
                                    } else {
                                      // Remove username
                                      setFilter({ ...filter, usernames: filter.usernames.filter(u => u !== username) });
                                    }
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`user-${username}`}>
                                  {username}
                                </label>
                              </div>
                            </div>
                          </li>
                        ))}
                      </>
                    </ul>
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Activity Type</label>
                  <select
                    className="form-select"
                    value={filter.activity_type}
                    onChange={(e) => {
                      const newFilter = { ...filter, activity_type: e.target.value };
                      setFilter(newFilter);
                    }}
                  >
                    <option value="">All Types</option>
                    <option value="navigation">Page Navigation</option>
                    <option value="api_call">API Calls</option>
                    <option value="login">Login Events</option>
                    <option value="logout">Logout Events</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Call Type</label>
                  <select
                    className="form-select"
                    value={filter.call_type}
                    onChange={(e) => {
                      const newFilter = { ...filter, call_type: e.target.value };
                      setFilter(newFilter);
                    }}
                  >
                    <option value="">All Call Types</option>
                    <option value="manual">Manual (User-Initiated)</option>
                    <option value="automatic">Automatic (System)</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Status Code</label>
                  <select
                    className="form-select"
                    value={filter.status_code}
                    onChange={(e) => {
                      const newFilter = { ...filter, status_code: e.target.value };
                      setFilter(newFilter);
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="200">200 - Success</option>
                    <option value="201">201 - Created</option>
                    <option value="400">400 - Bad Request</option>
                    <option value="401">401 - Unauthorized</option>
                    <option value="403">403 - Forbidden</option>
                    <option value="404">404 - Not Found</option>
                    <option value="500">500 - Server Error</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Results per page</label>
                  <select
                    className="form-select"
                    value={filter.limit}
                    onChange={(e) => {
                      const newFilter = { ...filter, limit: parseInt(e.target.value) };
                      setFilter(newFilter);
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Recent Activity</h5>
            </div>
            <div className="card-body">
              {loading && (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger">
                  Error loading activities: {error}
                </div>
              )}

              {!loading && !error && activities.length === 0 && (
                <div className="text-center text-muted">
                  No activities found.
                </div>
              )}

              {!loading && !error && activities.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th
                          onClick={() => handleSort('activity_type')}
                          style={{ cursor: 'pointer', userSelect: 'none', backgroundColor: '#f8f9fa' }}
                          title="Click to sort"
                        >
                          Type {getSortIcon('activity_type')}
                        </th>
                        <th
                          onClick={() => handleSort('call_type')}
                          style={{ cursor: 'pointer', userSelect: 'none', backgroundColor: '#f8f9fa' }}
                          title="Click to sort"
                        >
                          Call Type {getSortIcon('call_type')}
                        </th>
                        {(filter.usernames.includes('all') || filter.usernames.length > 1) && (
                          <th
                            onClick={() => handleSort('username')}
                            style={{ cursor: 'pointer', userSelect: 'none', backgroundColor: '#f8f9fa' }}
                            title="Click to sort"
                          >
                            User {getSortIcon('username')}
                          </th>
                        )}
                        <th
                          onClick={() => handleSort('endpoint')}
                          style={{ cursor: 'pointer', userSelect: 'none', backgroundColor: '#f8f9fa' }}
                          title="Click to sort"
                        >
                          Endpoint/Page {getSortIcon('endpoint')}
                        </th>
                        <th
                          onClick={() => handleSort('method')}
                          style={{ cursor: 'pointer', userSelect: 'none', backgroundColor: '#f8f9fa' }}
                          title="Click to sort"
                        >
                          Method {getSortIcon('method')}
                        </th>
                        <th
                          onClick={() => handleSort('timestamp')}
                          style={{ cursor: 'pointer', userSelect: 'none', backgroundColor: '#f8f9fa' }}
                          title="Click to sort"
                        >
                          Timestamp {getSortIcon('timestamp')}
                        </th>
                        <th
                          onClick={() => handleSort('duration_ms')}
                          style={{ cursor: 'pointer', userSelect: 'none', backgroundColor: '#f8f9fa' }}
                          title="Click to sort"
                        >
                          Duration {getSortIcon('duration_ms')}
                        </th>
                        <th
                          onClick={() => handleSort('status_code')}
                          style={{ cursor: 'pointer', userSelect: 'none', backgroundColor: '#f8f9fa' }}
                          title="Click to sort"
                        >
                          Status {getSortIcon('status_code')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedActivities.map((activity, index) => (
                        <tr key={activity.activity_id || index}>
                          <td>
                            <span className={`badge bg-${getActivityTypeColor(activity.activity_type)}`}>
                              {activity.activity_type}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge bg-${getCallTypeColor(activity.call_type)}`}
                              title={activity.call_type === 'manual' ? 'User-initiated action (button click, form submit, etc.)' :
                                activity.call_type === 'automatic' ? 'System-initiated action (page load, auto-refresh, etc.)' :
                                  'Call type not specified'}
                            >
                              {getCallTypeBadgeText(activity.call_type)}
                            </span>
                          </td>
                          {(filter.usernames.includes('all') || filter.usernames.length > 1) && (
                            <td>
                              <span className="badge bg-secondary">
                                {activity.username || 'Unknown'}
                              </span>
                            </td>
                          )}
                          <td>
                            <code>{activity.endpoint || 'N/A'}</code>
                          </td>
                          <td>
                            {activity.method && (
                              <span className="badge bg-secondary">{activity.method}</span>
                            )}
                          </td>
                          <td className="small">
                            {formatTimestamp(activity.timestamp)}
                          </td>
                          <td>
                            {activity.duration_ms ? `${activity.duration_ms}ms` : '-'}
                          </td>
                          <td>
                            {activity.status_code && (
                              <span className={`badge ${activity.status_code < 400 ? 'bg-success' : 'bg-danger'}`}>
                                {activity.status_code}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityDashboard;